/**
 * POS Cart Component
 * Shopping cart with totals, discounts, payment buttons, table checkout,
 * and Manager Override for comps, voids, and discounts
 */
import { useState, useEffect } from 'react';
import { 
  ShoppingBag, Plus, Minus, Trash2, X, DollarSign, CreditCard, 
  Banknote, Check, Loader2, Crown, Percent, Table2, Search, Users, User,
  Gift, MoreVertical, Ban, Shield, Smartphone, QrCode, Send, Wallet
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import POSUpsellReminder from '@/components/ordering/POSUpsellReminder';
import POSCoffeeClubRedemption from '@/components/ordering/POSCoffeeClubRedemption';
import ManagerOverrideModal from './ManagerOverrideModal';
import POSCashTenderModal from './POSCashTenderModal';
import { toast } from 'sonner';
import { posApi } from '@/utils/apiClient';

export default function POSCart({
  cart,
  customer,
  customerDiscount,
  appliedCashierDiscount,
  onUpdateQuantity,
  onRemoveItem,
  onClearCart,
  onAddItem,
  showPayment,
  setShowPayment,
  paymentMethod,
  setPaymentMethod,
  onProcessOrder,
  orderProcessing,
  onShowCashierDiscount,
  onRemoveCashierDiscount,
  upsellReminderCollapsed,
  setUpsellReminderCollapsed,
  orderType,
  tableNumber,
  staff, // Current logged-in staff for override tracking
  onCompItem, // Callback to mark item as comped
  onVoidItem, // Callback to void/remove item with override
  onApplyItemDiscount, // Callback to apply percentage discount to specific item
  onApplyItemDollarDiscount, // Callback to apply dollar amount discount to specific item
  isMobileDrawer = false, // When true, removes fixed width and adapts to container
  // Checkout discount buttons
  onStaffDiscount, // Opens Staff 50% modal
  onFamilyFriendsDiscount, // Opens F&F 15% modal
  // Dine-in specific
  onSendToKitchen, // Sends order to kitchen without payment (dine-in)
  isOwnerOrder = false, // Owner orders are $0 and print to stations only
}) {
  // Table Checkout State
  const [showTableCheckout, setShowTableCheckout] = useState(false);
  const [tableCheckoutNumber, setTableCheckoutNumber] = useState('');
  const [tableOrders, setTableOrders] = useState(null);
  const [loadingTableOrders, setLoadingTableOrders] = useState(false);
  const [processingTablePayment, setProcessingTablePayment] = useState(false);
  
  // M-PAiSA QR Code State
  const [mpaisaRefCode, setMpaisaRefCode] = useState('');
  
  // Cash Tender Modal State
  const [showCashTenderModal, setShowCashTenderModal] = useState(false);
  const [lastCashTransaction, setLastCashTransaction] = useState(null); // { amountTendered, changeDue }
  
  // Manager Override State
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideAction, setOverrideAction] = useState(null); // { type, item, discountPercent, discountAmount }
  const [activeItemMenu, setActiveItemMenu] = useState(null); // Index of item with open menu
  const [showDiscountInput, setShowDiscountInput] = useState(null); // Index of item showing discount input
  const [itemDiscountPercent, setItemDiscountPercent] = useState('');
  const [showDollarDiscountInput, setShowDollarDiscountInput] = useState(null); // Index of item showing dollar discount input
  const [itemDollarDiscount, setItemDollarDiscount] = useState('');
  
  // Split Payment State
  const [splitPaymentMode, setSplitPaymentMode] = useState(false);
  const [splitPayments, setSplitPayments] = useState([]); // Array of { method, amount }
  const [splitAmount, setSplitAmount] = useState('');
  
  // EFTPOS Overseas Card Surcharge State
  const [showEftposCardTypeModal, setShowEftposCardTypeModal] = useState(false);
  const [eftposCardType, setEftposCardType] = useState(null); // 'local' or 'overseas'
  const [overseasSurchargeAmount, setOverseasSurchargeAmount] = useState(0);
  const OVERSEAS_SURCHARGE_PERCENT = 4; // 4% surcharge for overseas cards
  
  // Referral Partner Code State
  const [partnerCode, setPartnerCode] = useState('');
  const [validatedPartner, setValidatedPartner] = useState(null);
  const [validatingCode, setValidatingCode] = useState(false);
  
  // Partner Balance Payment State
  const [partnerBalanceInfo, setPartnerBalanceInfo] = useState(null);
  const [usingPartnerBalance, setUsingPartnerBalance] = useState(false);
  
  // Fetch partner balance info when customer is a partner
  useEffect(() => {
    const fetchPartnerBalance = async () => {
      if (customer?.is_referral_partner && customer?.contact_id) {
        try {
          const res = await posApi.get(`/referral-partners/pos/partner-info/${customer.contact_id}`);
          if (res.data.is_partner) {
            setPartnerBalanceInfo(res.data);
          }
        } catch (err) {
          console.error('Failed to fetch partner balance:', err);
        }
      } else {
        setPartnerBalanceInfo(null);
      }
    };
    fetchPartnerBalance();
  }, [customer?.contact_id, customer?.is_referral_partner]);
  
  // Apply partner balance as payment
  const applyPartnerBalancePayment = async (amount) => {
    if (!customer?.contact_id || !partnerBalanceInfo?.can_use_balance) return;
    
    const useAmount = Math.min(parseFloat(amount) || 0, partnerBalanceInfo.partner?.commission_balance || 0);
    if (useAmount <= 0) {
      toast.error('Invalid amount');
      return;
    }
    
    setUsingPartnerBalance(true);
    try {
      const res = await posApi.post('/referral-partners/pos/use-partner-balance', {
        partner_id: customer.contact_id,
        amount: useAmount
      });
      
      if (res.data.success) {
        // Add as split payment
        setSplitPayments(prev => [...prev, {
          method: 'partner_balance',
          amount: useAmount,
          label: 'Partner Balance'
        }]);
        setSplitAmount('');
        
        // Update local balance info
        setPartnerBalanceInfo(prev => ({
          ...prev,
          partner: {
            ...prev.partner,
            commission_balance: res.data.remaining_balance
          },
          can_use_balance: res.data.remaining_balance > 0
        }));
        
        toast.success(`$${useAmount.toFixed(2)} deducted from partner balance`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to use partner balance');
    } finally {
      setUsingPartnerBalance(false);
    }
  };
  
  // Validate partner code and apply 5% discount
  const validatePartnerCode = async () => {
    if (!partnerCode.trim()) return;
    
    setValidatingCode(true);
    try {
      const response = await posApi.get(`/referral-partners/validate/${partnerCode.trim()}`);
      if (response.data.valid) {
        setValidatedPartner(response.data);
        toast.success(`Partner code valid! ${response.data.customer_discount_percent}% discount applied.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid partner code');
      setValidatedPartner(null);
    } finally {
      setValidatingCode(false);
    }
  };
  
  // Clear partner code
  const clearPartnerCode = () => {
    setPartnerCode('');
    setValidatedPartner(null);
  };
  
  // Calculate remaining amount for split payments
  const calculateSplitRemaining = () => {
    const paidSoFar = splitPayments.reduce((sum, p) => sum + p.amount, 0);
    return Math.max(0, totalWithVAT - paidSoFar);
  };
  
  // Split Payment - EFTPOS card type state
  const [splitEftposCardTypeMode, setSplitEftposCardTypeMode] = useState(false);
  const [pendingSplitAmount, setPendingSplitAmount] = useState('');
  
  // Add a split payment
  const addSplitPayment = (method, amount, eftposCardType = null, eftposSurcharge = 0) => {
    const remaining = calculateSplitRemaining();
    const baseAmount = Math.min(parseFloat(amount) || 0, remaining);
    
    if (baseAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    // Calculate final amount with surcharge for overseas EFTPOS
    const finalAmount = method === 'eftpos' && eftposCardType === 'overseas' 
      ? baseAmount + eftposSurcharge 
      : baseAmount;
    
    setSplitPayments(prev => [...prev, { 
      method, 
      amount: finalAmount,
      baseAmount: baseAmount,
      label: method === 'cash' ? 'Cash' : method === 'eftpos' ? 'EFTPOS' : 'M-PAiSA QR',
      eftpos_card_type: eftposCardType,
      eftpos_surcharge: eftposSurcharge
    }]);
    setSplitAmount('');
    setPendingSplitAmount('');
    setSplitEftposCardTypeMode(false);
    
    // Use baseAmount for remaining calculation (not surcharge)
    const newRemaining = remaining - baseAmount;
    if (newRemaining <= 0.01) {
      toast.success('Payment complete! Processing order...', { duration: 2000 });
    } else {
      if (eftposCardType === 'overseas' && eftposSurcharge > 0) {
        toast.success(`$${baseAmount.toFixed(2)} + $${eftposSurcharge.toFixed(2)} surcharge EFTPOS recorded. $${newRemaining.toFixed(2)} remaining.`);
      } else {
        toast.success(`$${finalAmount.toFixed(2)} ${method === 'cash' ? 'Cash' : method === 'eftpos' ? 'EFTPOS' : 'M-PAiSA'} recorded. $${newRemaining.toFixed(2)} remaining.`);
      }
    }
  };
  
  // Handle EFTPOS button click in split mode - show card type selection
  const handleSplitEftposClick = () => {
    if (!splitAmount || parseFloat(splitAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setPendingSplitAmount(splitAmount);
    setSplitEftposCardTypeMode(true);
  };
  
  // Handle card type selection in split mode
  const handleSplitCardTypeSelect = (cardType) => {
    const amount = parseFloat(pendingSplitAmount) || 0;
    const surcharge = cardType === 'overseas' ? amount * (OVERSEAS_SURCHARGE_PERCENT / 100) : 0;
    addSplitPayment('eftpos', pendingSplitAmount, cardType, surcharge);
  };
  
  // Remove a split payment
  const removeSplitPayment = (index) => {
    setSplitPayments(prev => prev.filter((_, i) => i !== index));
  };
  
  // Reset split payment
  const resetSplitPayment = () => {
    setSplitPaymentMode(false);
    setSplitPayments([]);
    setSplitAmount('');
    setSplitEftposCardTypeMode(false);
    setPendingSplitAmount('');
  };
  
  // Check if staff can perform action without override
  const canPerformWithoutOverride = (action) => {
    if (!staff) return false;
    const role = staff.role?.toLowerCase();
    // Owner and Manager can do everything without override
    if (role === 'owner' || role === 'manager' || role === 'mgr-sup') return true;
    // Supervisor can comp up to certain limits
    if (role === 'supervisor' && action === 'comp') return true;
    return false;
  };
  
  // Separate items: regular items, coffee club redemptions (free coffee), and coffee pass purchases
  const coffeePassPurchases = cart.filter(item => item.isCoffeePass);
  // Check for BOTH isCoffeeClubRedemption AND isCoffeePassRedemption (modal uses isCoffeePassRedemption)
  const coffeeClubRedemptions = cart.filter(item => item.isCoffeeClubRedemption || item.isCoffeePassRedemption);
  const regularItems = cart.filter(item => !item.isCoffeePass && !item.isCoffeeClubRedemption && !item.isCoffeePassRedemption);
  
  // Regular items are discountable
  const discountableItems = regularItems;
  const coffeePassItems = coffeeClubRedemptions;
  
  const discountableSubtotal = discountableItems.reduce((sum, item) => {
    const itemTotal = item.price * item.quantity;
    // Handle dollar discount OR percentage discount (not both)
    let itemDiscount = 0;
    if (item.dollarDiscount > 0) {
      // Dollar discount is per-unit, multiply by quantity
      itemDiscount = item.dollarDiscount * item.quantity;
    } else if (item.discountPercent > 0) {
      itemDiscount = itemTotal * (item.discountPercent / 100);
    }
    return sum + (item.isComped ? 0 : (itemTotal - itemDiscount));
  }, 0);
  
  // Coffee Pass purchases are NOT discountable - always $95 each (VAT-inclusive)
  const coffeePassPurchaseSubtotal = coffeePassPurchases.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  // Coffee club redemptions (free coffee) have $0 value
  const coffeePassSubtotal = coffeeClubRedemptions.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  const cartSubtotal = discountableSubtotal + coffeePassSubtotal + coffeePassPurchaseSubtotal;
  
  const loyaltyDiscountPercent = customerDiscount?.discount || 0;
  // Partner code gives 5% discount if no loyalty discount
  const partnerDiscountPercent = (validatedPartner && loyaltyDiscountPercent === 0) ? 5 : 0;
  const effectiveLoyaltyDiscount = loyaltyDiscountPercent > 0 ? loyaltyDiscountPercent : partnerDiscountPercent;
  const cashierDiscountPercentApplied = appliedCashierDiscount?.percent || 0;
  // Fixed dollar amount discount (e.g., promo code "$5 off")
  const cashierDiscountFixed = appliedCashierDiscount?.amount || 0;
  // Staff 50% discount bypasses the normal 30% cap
  const isStaffDiscount = appliedCashierDiscount?.type === 'staff_50';
  const maxDiscount = isStaffDiscount ? 50 : 30;
  const totalDiscountPercent = Math.min(effectiveLoyaltyDiscount + cashierDiscountPercentApplied, maxDiscount);
  // Only apply discounts to regular items, NOT to Coffee Pass purchases
  // Calculate percentage-based discount, then add any fixed dollar discount
  const percentageDiscount = discountableSubtotal * (totalDiscountPercent / 100);
  const discountAmount = percentageDiscount + cashierDiscountFixed;
  // Total = (discountable items - discount) + (coffee pass purchases) + (free redemptions @ $0)
  const baseCartTotal = Math.max(0, discountableSubtotal - discountAmount) + coffeePassPurchaseSubtotal + coffeePassSubtotal;
  // OWNER ORDERS: Total is always $0 - owner doesn't pay
  const cartTotal = isOwnerOrder ? 0 : baseCartTotal;
  
  // Calculate VAT - Fiji standard is 12.5%
  // For Bula Coffee Pass: $95 total is VAT-inclusive (base $84.44 + VAT)
  // Since menu price is now VAT-inclusive, we don't add extra VAT for Coffee Pass
  const VAT_RATE = 0.125;
  // Calculate total quantity of coffee passes (not just line items)
  const totalCoffeePassQuantity = coffeePassPurchases.reduce((sum, item) => sum + item.quantity, 0);
  const coffeePassVAT = 0; // VAT is already included in the $95 price
  // OWNER ORDERS: Total is always $0
  const totalWithVAT = isOwnerOrder ? 0 : (baseCartTotal + coffeePassVAT);

  // Handle comp item request
  const handleCompItem = (item, index) => {
    if (canPerformWithoutOverride('comp')) {
      // Manager/Owner can comp directly
      if (onCompItem) onCompItem(item.item_id, staff?.name, staff?.role);
      toast.success(`${item.name} comped by ${staff?.name}`);
    } else {
      // Cashier needs override
      setOverrideAction({ type: 'comp', item, index });
      setShowOverrideModal(true);
    }
    setActiveItemMenu(null);
  };

  // Handle void item request
  const handleVoidItem = (item, index) => {
    if (canPerformWithoutOverride('void')) {
      if (onVoidItem) onVoidItem(item.item_id, staff?.name, staff?.role);
      else onRemoveItem(item.item_id);
      toast.success(`${item.name} voided by ${staff?.name}`);
    } else {
      setOverrideAction({ type: 'void', item, index });
      setShowOverrideModal(true);
    }
    setActiveItemMenu(null);
  };

  // Handle discount item request
  const handleApplyDiscount = (item, index) => {
    const percent = parseFloat(itemDiscountPercent);
    if (isNaN(percent) || percent <= 0 || percent > 100) {
      toast.error('Enter a valid discount (1-100%)');
      return;
    }
    
    if (canPerformWithoutOverride('discount')) {
      if (onApplyItemDiscount) onApplyItemDiscount(item.item_id, percent, staff?.name, staff?.role);
      toast.success(`${percent}% discount applied to ${item.name}`);
      setShowDiscountInput(null);
      setItemDiscountPercent('');
    } else {
      setOverrideAction({ type: 'discount', item, index, discountPercent: percent });
      setShowOverrideModal(true);
      setShowDiscountInput(null);
    }
    setActiveItemMenu(null);
  };

  // Handle applying dollar discount to an item
  const handleApplyDollarDiscount = (item, index) => {
    const amount = parseFloat(itemDollarDiscount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Enter a valid discount amount');
      return;
    }
    
    if (amount >= item.price) {
      toast.error(`Discount cannot exceed item price ($${item.price.toFixed(2)})`);
      return;
    }
    
    if (canPerformWithoutOverride('discount')) {
      if (onApplyItemDollarDiscount) {
        onApplyItemDollarDiscount(item.item_id, amount, staff?.name, staff?.role);
        toast.success(`$${amount.toFixed(2)} discount applied to ${item.name}`);
      }
      setShowDollarDiscountInput(null);
      setItemDollarDiscount('');
    } else {
      setOverrideAction({ type: 'dollar_discount', item, index, discountAmount: amount });
      setShowOverrideModal(true);
      setShowDollarDiscountInput(null);
    }
    setActiveItemMenu(null);
  };

  // Handle override authorization
  const handleOverrideAuthorized = (authData) => {
    setShowOverrideModal(false);
    
    const { item } = overrideAction;
    
    if (authData.actionType === 'comp') {
      if (onCompItem) onCompItem(item.item_id, authData.managerName, authData.managerRole);
      toast.success(`${item.name} comped (Override: ${authData.managerName})`);
    } else if (authData.actionType === 'void') {
      if (onVoidItem) onVoidItem(item.item_id, authData.managerName, authData.managerRole);
      else onRemoveItem(item.item_id);
      toast.success(`${item.name} voided (Override: ${authData.managerName})`);
    } else if (authData.actionType === 'discount') {
      if (onApplyItemDiscount) {
        onApplyItemDiscount(item.item_id, overrideAction.discountPercent, authData.managerName, authData.managerRole);
      }
      toast.success(`${overrideAction.discountPercent}% discount applied (Override: ${authData.managerName})`);
      setItemDiscountPercent('');
    } else if (authData.actionType === 'dollar_discount') {
      if (onApplyItemDollarDiscount) {
        onApplyItemDollarDiscount(item.item_id, overrideAction.discountAmount, authData.managerName, authData.managerRole);
      }
      toast.success(`$${overrideAction.discountAmount.toFixed(2)} discount applied (Override: ${authData.managerName})`);
      setItemDollarDiscount('');
    }
    
    setOverrideAction(null);
  };

  // Fetch table orders for checkout
  const fetchTableOrders = async () => {
    if (!tableCheckoutNumber) {
      toast.error('Enter a table number');
      return;
    }
    
    const num = parseInt(tableCheckoutNumber);
    if (isNaN(num) || num < 1 || num > 20) {
      toast.error('Enter a valid table number (1-20)');
      return;
    }
    
    setLoadingTableOrders(true);
    try {
      const res = await posApi.get(`/tables/${num}/orders`);
      if (res.data.orders && res.data.orders.length > 0) {
        setTableOrders(res.data);
      } else {
        toast.error(`No unpaid orders for Table ${num}`);
        setTableOrders(null);
      }
    } catch (err) {
      toast.error('Table not found');
      setTableOrders(null);
      console.error(err);
    } finally {
      setLoadingTableOrders(false);
    }
  };

  // Process table payment
  const processTablePayment = async () => {
    if (!tableOrders) return;
    
    setProcessingTablePayment(true);
    try {
      // Mark all table orders as paid
      for (const order of tableOrders.orders) {
        await posApi.put(`/orders/${order.id}/status`, {
          payment_status: 'paid',
          payment_method: paymentMethod,
          paid_at: new Date().toISOString()
        });
      }
      
      // Clear table
      await posApi.post(`/tables/${tableOrders.table_number}/clear`);
      
      toast.success(`Table ${tableOrders.table_number} paid! Total: $${tableOrders.subtotal.toFixed(2)}`);
      setShowTableCheckout(false);
      setTableOrders(null);
      setTableCheckoutNumber('');
    } catch (err) {
      toast.error('Payment processing failed');
      console.error(err);
    } finally {
      setProcessingTablePayment(false);
    }
  };

  return (
    <div className={`${isMobileDrawer ? 'w-full' : 'w-80 lg:w-96 xl:w-[420px]'} bg-white dark:bg-gray-800 ${isMobileDrawer ? '' : 'border-l border-gray-200 dark:border-gray-700'} flex flex-col`}>
      {/* Cart Header */}
      <div className={`p-4 ${isMobileDrawer ? '' : 'border-b border-gray-200 dark:border-gray-700'}`}>
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" />
            {orderType === 'dine-in' && tableNumber ? `Table ${tableNumber}` : 'Current Order'}
          </h2>
          {cart.length > 0 && (
            <Button variant="ghost" size="sm" onClick={onClearCart} className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20">
              <Trash2 className="w-4 h-4 mr-1" />
              Clear Cart
            </Button>
          )}
        </div>
        {/* Order Type Badge */}
        {orderType && (
          <div className={`mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${
            orderType === 'dine-in' 
              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
              : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
          }`}>
            {orderType === 'dine-in' ? <Users className="w-3 h-3" /> : <ShoppingBag className="w-3 h-3" />}
            {orderType === 'dine-in' ? 'DINE-IN' : 'TAKEAWAY'}
          </div>
        )}
      </div>

      {/* Table Checkout Button */}
      <div className="px-4 pt-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowTableCheckout(!showTableCheckout)}
          className="w-full flex items-center justify-center gap-2 border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
          data-testid="table-checkout-btn"
        >
          <Table2 className="w-4 h-4" />
          Table Checkout
        </Button>
      </div>

      {/* Table Checkout Panel */}
      {showTableCheckout && (
        <div className="mx-4 mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2 flex items-center gap-2">
            <Table2 className="w-4 h-4" />
            Pay by Table
          </h3>
          
          {!tableOrders ? (
            <div className="flex gap-2">
              <Input
                type="number"
                placeholder="Table #"
                value={tableCheckoutNumber}
                onChange={(e) => setTableCheckoutNumber(e.target.value)}
                min={1}
                max={20}
                className="flex-1 h-9 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                data-testid="table-checkout-input"
              />
              <Button
                size="sm"
                onClick={fetchTableOrders}
                disabled={loadingTableOrders}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="table-checkout-search"
              >
                {loadingTableOrders ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Table Orders Summary */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Table</span>
                  <span className="font-bold text-gray-900 dark:text-white">{tableOrders.table_number}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Orders</span>
                  <span className="font-medium text-gray-900 dark:text-white">{tableOrders.order_count}</span>
                </div>
                {tableOrders.dine_in_count > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-blue-600 dark:text-blue-400">Dine-in ({tableOrders.dine_in_count})</span>
                    <span className="text-blue-600 dark:text-blue-400">${tableOrders.dine_in_total?.toFixed(2)}</span>
                  </div>
                )}
                {tableOrders.takeaway_count > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-orange-600 dark:text-orange-400">Takeaway ({tableOrders.takeaway_count})</span>
                    <span className="text-orange-600 dark:text-orange-400">${tableOrders.takeaway_total?.toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-2 flex justify-between">
                  <span className="font-semibold text-gray-900 dark:text-white">Total</span>
                  <span className="font-bold text-lg text-gray-900 dark:text-white">${tableOrders.subtotal.toFixed(2)}</span>
                </div>
              </div>
              
              {/* Payment Method Selection */}
              <div className="grid grid-cols-3 gap-2">
                <Button
                  variant={paymentMethod === 'cash' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPaymentMethod('cash')}
                  className={paymentMethod === 'cash' ? 'bg-green-600 hover:bg-green-700 text-white' : 'text-gray-700 dark:text-gray-300'}
                >
                  <Banknote className="w-4 h-4 mr-1" /> Cash
                </Button>
                <Button
                  variant={paymentMethod === 'eftpos' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPaymentMethod('eftpos')}
                  className={paymentMethod === 'eftpos' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'text-gray-700 dark:text-gray-300'}
                >
                  <CreditCard className="w-4 h-4 mr-1" /> EFTPOS
                </Button>
                <Button
                  variant={paymentMethod === 'mpaisa_qr' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPaymentMethod('mpaisa_qr')}
                  className={paymentMethod === 'mpaisa_qr' ? 'bg-red-700 hover:bg-red-800 text-white' : 'text-gray-700 dark:text-gray-300'}
                >
                  <QrCode className="w-4 h-4 mr-1" /> M-PAiSA
                </Button>
              </div>
              
              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setTableOrders(null); setTableCheckoutNumber(''); }}
                  className="text-gray-700 dark:text-gray-300"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={processTablePayment}
                  disabled={processingTablePayment}
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                  data-testid="table-checkout-pay"
                >
                  {processingTablePayment ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-1" /> Pay
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Cart Items - Scrollable Area */}
      <div className={`flex-1 overflow-y-auto p-4 ${showPayment ? 'pb-2' : ''}`}>
        {cart.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingBag className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">No items in order</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Upsell Reminder for Servers */}
            <POSUpsellReminder
              cartItems={cart}
              onAddSuggestion={(item) => {
                onAddItem(item);
                toast.success(`Added ${item.name}`, { duration: 1500 });
              }}
              collapsed={upsellReminderCollapsed}
              onToggleCollapse={() => setUpsellReminderCollapsed(!upsellReminderCollapsed)}
            />
            
            {/* Coffee Club Redemption */}
            <POSCoffeeClubRedemption
              customer={customer}
              compact={true}
              onAddFreeCoffee={(coffeeItem) => {
                const hasRedemption = cart.some(c => c.isCoffeeClubRedemption || c.isCoffeePassRedemption);
                if (hasRedemption) {
                  toast.error('Already added free coffee to this order');
                  return;
                }
                onAddItem({
                  ...coffeeItem,
                  isCoffeeClubRedemption: true,
                  isCoffeePassRedemption: true  // Set both for compatibility
                });
              }}
            />
            
            {cart.map((item, idx) => (
              <div 
                key={`${item.item_id}-${idx}`} 
                className={`bg-gray-50 dark:bg-gray-700 rounded-lg p-3 relative ${
                  item.isComped ? 'border-2 border-green-500 dark:border-green-400' : ''
                } ${item.isVoided ? 'opacity-50 border-2 border-red-500' : ''}`}
              >
                {/* Comped Badge */}
                {item.isComped && (
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Gift className="w-3 h-3" /> COMP
                  </div>
                )}
                
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h4 className={`font-medium text-gray-900 dark:text-white text-sm ${item.isComped ? 'line-through' : ''}`}>
                      {item.name}
                    </h4>
                    {/* Item-level discount badge */}
                    {item.discountPercent > 0 && !item.isComped && (
                      <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                        <Percent className="w-3 h-3" /> {item.discountPercent}% off
                        {item.discountBy && <span className="text-gray-500">({item.discountBy})</span>}
                      </span>
                    )}
                    {item.compedBy && (
                      <span className="text-xs text-green-600 dark:text-green-400">
                        Comped by {item.compedBy}
                      </span>
                    )}
                  </div>
                  
                  {/* Quick Remove Button */}
                  <button
                    onClick={() => onRemoveItem(item.item_id)}
                    className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors mr-1"
                    title="Remove item"
                    data-testid={`remove-item-${idx}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-400 hover:text-red-600 dark:text-red-500 dark:hover:text-red-400" />
                  </button>
                  
                  {/* Actions Menu */}
                  <div className="relative">
                    <button 
                      onClick={() => setActiveItemMenu(activeItemMenu === idx ? null : idx)}
                      className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                      data-testid={`cart-item-menu-${idx}`}
                    >
                      <MoreVertical className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    </button>
                    
                    {/* Dropdown Menu */}
                    {activeItemMenu === idx && (
                      <div className="absolute right-0 top-8 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 py-1 z-20 min-w-[160px]">
                        {!item.isComped && (
                          <>
                            <button
                              onClick={() => handleCompItem(item, idx)}
                              className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-green-600 dark:text-green-400"
                              data-testid={`comp-item-${idx}`}
                            >
                              <Gift className="w-4 h-4" />
                              Comp Item
                              {!canPerformWithoutOverride('comp') && (
                                <Shield className="w-3 h-3 ml-auto text-amber-500" title="Override required" />
                              )}
                            </button>
                            
                            <button
                              onClick={() => {
                                setShowDiscountInput(idx);
                                setShowDollarDiscountInput(null);
                                setActiveItemMenu(null);
                              }}
                              className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-blue-600 dark:text-blue-400"
                              data-testid={`discount-item-${idx}`}
                            >
                              <Percent className="w-4 h-4" />
                              % Discount
                              {!canPerformWithoutOverride('discount') && (
                                <Shield className="w-3 h-3 ml-auto text-amber-500" title="Override required" />
                              )}
                            </button>
                            
                            <button
                              onClick={() => {
                                setShowDollarDiscountInput(idx);
                                setShowDiscountInput(null);
                                setActiveItemMenu(null);
                              }}
                              className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-green-600 dark:text-green-400"
                              data-testid={`dollar-discount-item-${idx}`}
                            >
                              <DollarSign className="w-4 h-4" />
                              $ Discount
                              {!canPerformWithoutOverride('discount') && (
                                <Shield className="w-3 h-3 ml-auto text-amber-500" title="Override required" />
                              )}
                            </button>
                          </>
                        )}
                        
                        <button
                          onClick={() => handleVoidItem(item, idx)}
                          className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-red-600 dark:text-red-400"
                          data-testid={`void-item-${idx}`}
                        >
                          <Ban className="w-4 h-4" />
                          Void Item
                          {!canPerformWithoutOverride('void') && (
                            <Shield className="w-3 h-3 ml-auto text-amber-500" title="Override required" />
                          )}
                        </button>
                        
                        <button
                          onClick={() => { onRemoveItem(item.item_id); setActiveItemMenu(null); }}
                          className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
                        >
                          <Trash2 className="w-4 h-4" />
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Discount Input (shown when Add Discount is clicked) */}
                {showDiscountInput === idx && (
                  <div className="mb-2 flex items-center gap-2 bg-blue-50 dark:bg-blue-900/30 p-2 rounded-lg">
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={itemDiscountPercent}
                      onChange={(e) => setItemDiscountPercent(e.target.value)}
                      placeholder="% off"
                      className="w-20 h-8 text-sm"
                      data-testid={`discount-input-${idx}`}
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-400">%</span>
                    <Button
                      size="sm"
                      onClick={() => handleApplyDiscount(item, idx)}
                      className="h-8 bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid={`apply-discount-${idx}`}
                    >
                      Apply
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setShowDiscountInput(null); setItemDiscountPercent(''); }}
                      className="h-8"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                
                {/* Dollar Discount Input */}
                {showDollarDiscountInput === idx && (
                  <div className="mb-2 flex items-center gap-2 bg-green-50 dark:bg-green-900/30 p-2 rounded-lg">
                    <span className="text-sm text-gray-600 dark:text-gray-400">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={item.price - 0.01}
                      value={itemDollarDiscount}
                      onChange={(e) => setItemDollarDiscount(e.target.value)}
                      placeholder="Amount off"
                      className="w-24 h-8 text-sm"
                      data-testid={`dollar-discount-input-${idx}`}
                    />
                    <Button
                      size="sm"
                      onClick={() => handleApplyDollarDiscount(item, idx)}
                      className="h-8 bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`apply-dollar-discount-${idx}`}
                    >
                      Apply
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setShowDollarDiscountInput(null); setItemDollarDiscount(''); }}
                      className="h-8"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onUpdateQuantity(item.item_id, -1)}
                      className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-200"
                      disabled={item.isComped}
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="font-medium text-gray-900 dark:text-white w-6 text-center">{item.quantity}</span>
                    <button
                      onClick={() => onUpdateQuantity(item.item_id, 1)}
                      className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-200"
                      disabled={item.isComped}
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="text-right">
                    {item.isComped ? (
                      <span className="font-medium text-green-600 dark:text-green-400">$0.00</span>
                    ) : item.dollarDiscount > 0 ? (
                      <div>
                        <span className="text-xs text-gray-400 line-through">${(item.price * item.quantity).toFixed(2)}</span>
                        <span className="font-medium text-gray-900 dark:text-white ml-1">
                          ${((item.price - item.dollarDiscount) * item.quantity).toFixed(2)}
                        </span>
                        <span className="text-xs text-green-600 dark:text-green-400 block">-${item.dollarDiscount.toFixed(2)} off</span>
                      </div>
                    ) : item.discountPercent > 0 ? (
                      <div>
                        <span className="text-xs text-gray-400 line-through">${(item.price * item.quantity).toFixed(2)}</span>
                        <span className="font-medium text-gray-900 dark:text-white ml-1">
                          ${((item.price * item.quantity) * (1 - item.discountPercent / 100)).toFixed(2)}
                        </span>
                      </div>
                    ) : (
                      <span className="font-medium text-gray-900 dark:text-white">${(item.price * item.quantity).toFixed(2)}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cart Totals & Payment - Fixed at bottom with scrollable payment options */}
      <div className={`p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 ${showPayment ? 'max-h-[70vh] overflow-y-auto' : ''}`}>
        {/* Totals */}
        <div className="space-y-2 mb-4">
          <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
            <span>Subtotal</span>
            <span>${cartSubtotal.toFixed(2)}</span>
          </div>
          
          {/* Loyalty Discount */}
          {loyaltyDiscountPercent > 0 && (
            <div className="flex justify-between text-sm text-teal-600">
              <span className="flex items-center gap-1">
                <Crown className="w-3 h-3" />
                {customerDiscount?.tier ? `${customerDiscount.tier} Member` : 'Founding Member'} ({loyaltyDiscountPercent}%)
              </span>
              <span>-${(discountableSubtotal * loyaltyDiscountPercent / 100).toFixed(2)}</span>
            </div>
          )}
          
          {/* Cashier One-Time Discount / Staff Discount / Promo Code */}
          {appliedCashierDiscount && (appliedCashierDiscount.percent > 0 || appliedCashierDiscount.amount > 0) && (
            <div className={`flex justify-between text-sm ${
              appliedCashierDiscount.type === 'staff_50' 
                ? 'text-purple-600 dark:text-purple-400 font-semibold' 
                : appliedCashierDiscount.type === 'family_friends'
                  ? 'text-rose-600 dark:text-rose-400'
                  : appliedCashierDiscount.type === 'manual'
                    ? 'text-blue-600 dark:text-blue-400'
                    : appliedCashierDiscount.type === 'promo'
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-amber-600'
            }`}>
              <span className="flex items-center gap-1">
                <Percent className="w-3 h-3" />
                {appliedCashierDiscount.type === 'staff_50' 
                  ? `Staff 50% (${appliedCashierDiscount.staffName || 'Staff'})` 
                  : appliedCashierDiscount.type === 'family_friends'
                    ? `Family & Friends (${appliedCashierDiscount.percent || 0}%)`
                    : appliedCashierDiscount.type === 'manual'
                      ? `Manual Discount (${appliedCashierDiscount.percent || 0}%)`
                      : appliedCashierDiscount.type === 'promo'
                        ? `Promo: ${appliedCashierDiscount.code || ''}`
                        : `Cashier Discount (${appliedCashierDiscount.percent || 0}%)`
                }
                <button 
                  onClick={onRemoveCashierDiscount}
                  className="ml-1 text-red-500 hover:text-red-700"
                  title="Remove discount"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
              <span>-${(appliedCashierDiscount.percent > 0 
                ? (discountableSubtotal * (appliedCashierDiscount.percent || 0) / 100)
                : (appliedCashierDiscount.amount || 0)
              ).toFixed(2)}</span>
            </div>
          )}
          
          {/* Add Cashier Discount Button */}
          {!appliedCashierDiscount && cart.length > 0 && loyaltyDiscountPercent === 0 && !validatedPartner && (
            <button
              onClick={onShowCashierDiscount}
              className="w-full text-sm text-amber-600 hover:text-amber-700 flex items-center justify-center gap-1 py-1"
              data-testid="add-cashier-discount-btn"
            >
              <Plus className="w-3 h-3" />
              Add One-Time Discount (max 10%, non-loyalty only)
            </button>
          )}
          
          {/* Partner Code Discount Display */}
          {validatedPartner && (
            <div className="flex justify-between text-sm text-emerald-600 dark:text-emerald-400">
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                Partner: {validatedPartner.partner_name} ({validatedPartner.customer_discount_percent}%)
                <button 
                  onClick={clearPartnerCode}
                  className="ml-1 text-red-500 hover:text-red-700"
                  title="Remove partner code"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
              <span>-${(discountableSubtotal * 0.05).toFixed(2)}</span>
            </div>
          )}
          
          {/* Partner Code Input - show only when no loyalty discount and no partner code validated */}
          {!validatedPartner && loyaltyDiscountPercent === 0 && cart.length > 0 && (
            <div className="flex gap-2 mt-2">
              <Input
                placeholder="Partner code (BULA-XXXX)"
                value={partnerCode}
                onChange={(e) => setPartnerCode(e.target.value.toUpperCase())}
                className="h-8 text-sm"
                data-testid="partner-code-input"
                onKeyDown={(e) => e.key === 'Enter' && validatePartnerCode()}
              />
              <Button
                size="sm"
                onClick={validatePartnerCode}
                disabled={validatingCode || !partnerCode.trim()}
                className="h-8 bg-emerald-600 hover:bg-emerald-700"
                data-testid="validate-partner-code-btn"
              >
                {validatingCode ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply'}
              </Button>
            </div>
          )}
          
          {/* Show VAT breakdown if Coffee Pass in cart */}
          {coffeePassPurchases.length > 0 && (
            <div className="flex justify-between text-sm text-amber-600 dark:text-amber-400">
              <span>Coffee Pass VAT ({totalCoffeePassQuantity}x $5)</span>
              <span>+${coffeePassVAT.toFixed(2)}</span>
            </div>
          )}
          
          <div className="flex justify-between text-xl font-bold text-gray-900 dark:text-white pt-2 border-t border-gray-200 dark:border-gray-700">
            <span>Total{totalCoffeePassQuantity > 0 ? ' (incl. VAT)' : ''}</span>
            <span>${totalWithVAT.toFixed(2)}</span>
          </div>
        </div>

        {/* Checkout Discount Buttons - Prominent placement */}
        {cart.length > 0 && !appliedCashierDiscount && !showPayment && (
          <div className="mb-3 p-3 lg:p-4 bg-gradient-to-r from-purple-50 to-rose-50 dark:from-purple-900/20 dark:to-rose-900/20 rounded-xl border border-purple-200 dark:border-purple-800">
            <p className="text-xs lg:text-sm text-gray-600 dark:text-gray-400 mb-2 text-center font-medium">Apply Discount Before Payment</p>
            <div className="grid grid-cols-2 gap-2 lg:gap-3">
              <Button
                variant="outline"
                onClick={onStaffDiscount}
                className="h-14 lg:h-16 bg-purple-100 hover:bg-purple-200 dark:bg-purple-900/40 dark:hover:bg-purple-900/60 border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 font-semibold"
                data-testid="cart-staff-discount-btn"
              >
                <User className="w-5 h-5 lg:w-6 lg:h-6 mr-2" />
                <span className="text-sm lg:text-base">Staff 50%</span>
              </Button>
              <Button
                variant="outline"
                onClick={onFamilyFriendsDiscount}
                className="h-14 lg:h-16 bg-rose-100 hover:bg-rose-200 dark:bg-rose-900/40 dark:hover:bg-rose-900/60 border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-300 font-semibold"
                data-testid="cart-ff-discount-btn"
              >
                <Users className="w-5 h-5 lg:w-6 lg:h-6 mr-2" />
                <span className="text-sm lg:text-base">F&F 15%</span>
              </Button>
            </div>
          </div>
        )}

        {/* Payment Buttons */}
        {!showPayment ? (
          orderType === 'dine-in' && tableNumber ? (
            // Dine-in: Show "Send to Stations" button (order goes to KDS, payment later)
            <div className="space-y-2">
              <Button
                onClick={onSendToKitchen}
                disabled={cart.length === 0}
                className="w-full h-16 lg:h-20 text-lg lg:text-xl bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50"
                data-testid="pos-send-order-btn"
              >
                <Send className="w-6 h-6 lg:w-7 lg:h-7 mr-2" />
                Send to Stations - Table {tableNumber}
              </Button>
              <p className="text-xs text-center text-gray-500 dark:text-gray-400">
                Order prints to stations. Customer pays when ready.
              </p>
            </div>
          ) : (
            // Regular: Show Pay button
            <Button
              onClick={() => setShowPayment(true)}
              disabled={cart.length === 0}
              className="w-full h-16 lg:h-20 text-lg lg:text-xl bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50"
              data-testid="pos-pay-btn"
            >
              <DollarSign className="w-6 h-6 lg:w-7 lg:h-7 mr-2" />
              Pay ${totalWithVAT.toFixed(2)}
            </Button>
          )
        ) : splitPaymentMode ? (
          /* SPLIT PAYMENT MODE */
          <div className="space-y-3">
            {/* Split Payment Header */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg p-3 border border-purple-200 dark:border-purple-700">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-purple-700 dark:text-purple-300 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Split Payment
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetSplitPayment}
                  className="text-red-500 hover:text-red-700 h-6 px-2"
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancel Split
                </Button>
              </div>
              
              {/* Progress */}
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Total:</span>
                  <span className="font-bold">${totalWithVAT.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-green-600 dark:text-green-400">Paid:</span>
                  <span className="font-bold text-green-600">${splitPayments.reduce((s, p) => s + p.amount, 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className={calculateSplitRemaining() > 0 ? "text-amber-600 dark:text-amber-400" : "text-green-600"}>Remaining:</span>
                  <span className={`font-bold ${calculateSplitRemaining() > 0 ? "text-amber-600" : "text-green-600"}`}>
                    ${calculateSplitRemaining().toFixed(2)}
                  </span>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="mt-2 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-green-500 to-teal-500 transition-all duration-300"
                  style={{ width: `${Math.min(100, (splitPayments.reduce((s, p) => s + p.amount, 0) / totalWithVAT) * 100)}%` }}
                />
              </div>
            </div>
            
            {/* Recorded Payments List */}
            {splitPayments.length > 0 && (
              <div className="space-y-1">
                {splitPayments.map((payment, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-green-50 dark:bg-green-900/20 rounded-lg px-3 py-2 text-sm">
                    <div className="flex items-center gap-2">
                      {payment.method === 'cash' ? (
                        <Banknote className="w-4 h-4 text-green-600" />
                      ) : payment.method === 'eftpos' ? (
                        <CreditCard className="w-4 h-4 text-blue-600" />
                      ) : payment.method === 'partner_balance' ? (
                        <Wallet className="w-4 h-4 text-purple-600" />
                      ) : (
                        <Smartphone className="w-4 h-4 text-red-600" />
                      )}
                      <div className="flex flex-col">
                        <span className="text-gray-700 dark:text-gray-300">
                          {payment.label}
                          {payment.eftpos_card_type === 'overseas' && ' (Overseas)'}
                        </span>
                        {payment.eftpos_surcharge > 0 && (
                          <span className="text-xs text-amber-600">
                            Incl. ${payment.eftpos_surcharge.toFixed(2)} surcharge
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-green-600">${payment.amount.toFixed(2)}</span>
                      <button
                        onClick={() => removeSplitPayment(idx)}
                        className="text-red-400 hover:text-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add Payment - Only show if there's remaining amount */}
            {calculateSplitRemaining() > 0.01 && !splitEftposCardTypeMode && (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max={calculateSplitRemaining()}
                    placeholder={`Amount (max $${calculateSplitRemaining().toFixed(2)})`}
                    value={splitAmount}
                    onChange={(e) => setSplitAmount(e.target.value)}
                    className="flex-1"
                    data-testid="split-payment-amount"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSplitAmount(calculateSplitRemaining().toFixed(2))}
                    className="px-3 text-xs"
                  >
                    All
                  </Button>
                </div>
                
                {/* Payment Method Buttons for Split */}
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    onClick={() => addSplitPayment('cash', splitAmount)}
                    disabled={!splitAmount || parseFloat(splitAmount) <= 0}
                    className="bg-green-600 hover:bg-green-700 h-12"
                    data-testid="split-add-cash"
                  >
                    <Banknote className="w-4 h-4 mr-1" />
                    Cash
                  </Button>
                  <Button
                    onClick={handleSplitEftposClick}
                    disabled={!splitAmount || parseFloat(splitAmount) <= 0}
                    className="bg-blue-600 hover:bg-blue-700 h-12"
                    data-testid="split-add-eftpos"
                  >
                    <CreditCard className="w-4 h-4 mr-1" />
                    EFTPOS
                  </Button>
                  <Button
                    onClick={() => addSplitPayment('mpaisa_qr', splitAmount)}
                    disabled={!splitAmount || parseFloat(splitAmount) <= 0}
                    className="bg-red-700 hover:bg-red-800 h-12"
                    data-testid="split-add-mpaisa-qr"
                  >
                    <QrCode className="w-4 h-4 mr-1" />
                    M-PAiSA
                  </Button>
                </div>
                
                {/* Partner Balance Payment Button - Only show if customer is a partner with balance */}
                {partnerBalanceInfo?.can_use_balance && (
                  <div className="mt-2 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-700">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Wallet className="w-5 h-5 text-purple-600" />
                        <span className="font-medium text-purple-700 dark:text-purple-300">
                          Partner Balance
                        </span>
                      </div>
                      <span className="font-bold text-purple-600">
                        ${partnerBalanceInfo.partner?.commission_balance?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                    <Button
                      onClick={() => applyPartnerBalancePayment(splitAmount || calculateSplitRemaining())}
                      disabled={usingPartnerBalance || !partnerBalanceInfo.partner?.commission_balance}
                      className="w-full bg-purple-600 hover:bg-purple-700 h-10"
                      data-testid="split-add-partner-balance"
                    >
                      {usingPartnerBalance ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Wallet className="w-4 h-4 mr-1" />
                          Use ${Math.min(
                            parseFloat(splitAmount) || calculateSplitRemaining(),
                            partnerBalanceInfo.partner?.commission_balance || 0
                          ).toFixed(2)} from Balance
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}
            
            {/* EFTPOS Card Type Selection for Split Payment */}
            {splitEftposCardTypeMode && (
              <div className="space-y-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-blue-700 dark:text-blue-300 flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    EFTPOS Card Type
                  </h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSplitEftposCardTypeMode(false);
                      setPendingSplitAmount('');
                    }}
                    className="text-gray-500 h-6 px-2"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="text-center py-2">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Amount: <span className="font-bold text-lg">${parseFloat(pendingSplitAmount).toFixed(2)}</span></p>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {/* Local Card */}
                  <button
                    onClick={() => handleSplitCardTypeSelect('local')}
                    className="flex flex-col items-center justify-center p-4 rounded-xl border-2 border-green-500 bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50 transition-all"
                    data-testid="split-eftpos-local"
                  >
                    <span className="text-2xl mb-1">🇫🇯</span>
                    <span className="font-bold text-green-700 dark:text-green-400">Local Card</span>
                    <span className="text-xs text-green-600 dark:text-green-500">No surcharge</span>
                    <span className="font-bold text-green-700 mt-1">${parseFloat(pendingSplitAmount).toFixed(2)}</span>
                  </button>
                  
                  {/* Overseas Card */}
                  <button
                    onClick={() => handleSplitCardTypeSelect('overseas')}
                    className="flex flex-col items-center justify-center p-4 rounded-xl border-2 border-amber-500 bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-all"
                    data-testid="split-eftpos-overseas"
                  >
                    <span className="text-2xl mb-1">🌏</span>
                    <span className="font-bold text-amber-700 dark:text-amber-400">Overseas Card</span>
                    <span className="text-xs text-amber-600 dark:text-amber-500">+4% surcharge</span>
                    <span className="text-xs text-amber-600">+${(parseFloat(pendingSplitAmount) * OVERSEAS_SURCHARGE_PERCENT / 100).toFixed(2)}</span>
                    <span className="font-bold text-amber-700 mt-1">${(parseFloat(pendingSplitAmount) * (1 + OVERSEAS_SURCHARGE_PERCENT / 100)).toFixed(2)}</span>
                  </button>
                </div>
                
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  Overseas Visa/MasterCard cards incur a 4% bank surcharge
                </p>
              </div>
            )}
            
            {/* Complete Split Payment Button */}
            {calculateSplitRemaining() <= 0.01 && splitPayments.length > 0 && (
              <Button
                onClick={() => {
                  // Process order with split payment data
                  onProcessOrder(null, null, splitPayments, validatedPartner?.code || null);
                  resetSplitPayment();
                }}
                disabled={orderProcessing}
                className="w-full h-14 bg-teal-600 hover:bg-teal-700 text-white text-lg"
                data-testid="split-complete-order"
              >
                {orderProcessing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Complete Order (Split Payment)
                  </>
                )}
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {/* Split Payment Toggle Button */}
            <Button
              variant="outline"
              onClick={() => setSplitPaymentMode(true)}
              className="w-full h-10 border-purple-300 text-purple-700 dark:border-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20"
              data-testid="pos-split-payment-btn"
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Split Payment (Cash + M-PAiSA)
            </Button>
            
            {/* Partner Balance Quick Pay - Show if customer is a partner with balance */}
            {partnerBalanceInfo?.can_use_balance && partnerBalanceInfo.partner?.commission_balance >= totalWithVAT && (
              <Button
                onClick={async () => {
                  await applyPartnerBalancePayment(totalWithVAT);
                  // If full amount paid from balance, trigger order completion
                  // Need to go through split payment flow
                  setSplitPaymentMode(true);
                }}
                disabled={usingPartnerBalance}
                className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="pos-pay-partner-balance"
              >
                {usingPartnerBalance ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Wallet className="w-5 h-5 mr-2" />
                    Pay with Partner Balance (${partnerBalanceInfo.partner?.commission_balance?.toFixed(2)})
                  </>
                )}
              </Button>
            )}
            
            {/* Partner Balance Partial - Show if they have some balance but not enough */}
            {partnerBalanceInfo?.can_use_balance && partnerBalanceInfo.partner?.commission_balance > 0 && partnerBalanceInfo.partner?.commission_balance < totalWithVAT && (
              <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-700">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-purple-700 dark:text-purple-300">
                    <Wallet className="w-4 h-4" />
                    Partner Balance Available
                  </span>
                  <span className="font-bold text-purple-600">${partnerBalanceInfo.partner?.commission_balance?.toFixed(2)}</span>
                </div>
                <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                  Use Split Payment to apply balance + another payment method
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-3 gap-2">
              <Button
                variant={paymentMethod === 'cash' ? 'default' : 'outline'}
                onClick={() => setPaymentMethod('cash')}
                className={`h-12 lg:h-14 ${paymentMethod === 'cash' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                data-testid="pos-pay-cash"
              >
                <Banknote className="w-5 h-5 mr-1 lg:w-6 lg:h-6" />
                <span className="text-sm lg:text-base">Cash</span>
              </Button>
              <Button
                variant={paymentMethod === 'eftpos' ? 'default' : 'outline'}
                onClick={() => {
                  // Show card type selection modal
                  setShowEftposCardTypeModal(true);
                }}
                className={`h-12 lg:h-14 ${paymentMethod === 'eftpos' ? 'bg-blue-600 hover:bg-blue-700' : 'border-blue-500 text-blue-600 dark:text-blue-400'}`}
                data-testid="pos-pay-eftpos"
              >
                <CreditCard className="w-5 h-5 mr-1 lg:w-6 lg:h-6" />
                <span className="text-sm lg:text-base">EFTPOS</span>
              </Button>
              <Button
                variant={paymentMethod === 'mpaisa_qr' ? 'default' : 'outline'}
                onClick={() => setPaymentMethod('mpaisa_qr')}
                className={`h-12 lg:h-14 ${paymentMethod === 'mpaisa_qr' ? 'bg-red-700 hover:bg-red-800' : 'border-red-500 text-red-600 dark:text-red-400'}`}
                data-testid="pos-pay-mpaisa-qr"
              >
                <QrCode className="w-5 h-5 mr-1 lg:w-6 lg:h-6" />
                <span className="text-sm lg:text-base">M-PAiSA</span>
              </Button>
            </div>
            
            {/* EFTPOS Info */}
            {paymentMethod === 'eftpos' && (
              <div className={`rounded-lg p-4 border ${eftposCardType === 'overseas' ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800' : 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800'}`}>
                <div className="text-center">
                  <CreditCard className={`w-8 h-8 mx-auto mb-2 ${eftposCardType === 'overseas' ? 'text-amber-600 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400'}`} />
                  <p className={`font-medium ${eftposCardType === 'overseas' ? 'text-amber-700 dark:text-amber-300' : 'text-blue-700 dark:text-blue-300'}`}>
                    {eftposCardType === 'overseas' ? '🌏 Overseas Card' : '🇫🇯 Local Card'} - EFTPOS Payment
                  </p>
                  {eftposCardType === 'overseas' && overseasSurchargeAmount > 0 && (
                    <div className="mt-2 p-2 bg-amber-100 dark:bg-amber-900/40 rounded-lg">
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        Original: ${totalWithVAT.toFixed(2)} + 4% Surcharge: ${overseasSurchargeAmount.toFixed(2)}
                      </p>
                      <p className="text-lg font-bold text-amber-800 dark:text-amber-200 mt-1">
                        Total: ${(totalWithVAT + overseasSurchargeAmount).toFixed(2)}
                      </p>
                    </div>
                  )}
                  {eftposCardType === 'local' && (
                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                      Process ${totalWithVAT.toFixed(2)} on the ANZ terminal
                    </p>
                  )}
                  <button
                    onClick={() => setShowEftposCardTypeModal(true)}
                    className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mt-2 underline"
                  >
                    Change card type
                  </button>
                </div>
              </div>
            )}
            
            {/* M-PAiSA QR Code Section - for in-person payments */}
            {paymentMethod === 'mpaisa_qr' && (
              <div className="bg-red-50 dark:bg-red-950/30 rounded-lg p-4 border border-red-200 dark:border-red-800">
                <div className="text-center mb-3">
                  <p className="font-medium text-red-700 dark:text-red-300">Customer scans QR code & pays ${totalWithVAT.toFixed(2)}</p>
                  <p className="text-sm text-red-600 dark:text-red-400">Then enter their confirmation code below</p>
                </div>
                <div className="flex justify-center mb-3">
                  <img 
                    src="/mpaisa-qr-code.png" 
                    alt="M-PAiSA QR Code" 
                    className="w-32 h-32 border-2 border-red-300 dark:border-red-700 rounded-lg"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://api.qrserver.com/v1/create-qr-code/?size=128x128&data=mpaisa://pay/9290013';
                    }}
                  />
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={12}
                  placeholder="Enter 10-digit code"
                  value={mpaisaRefCode}
                  onChange={(e) => {
                    // Only allow digits
                    const value = e.target.value.replace(/\D/g, '');
                    setMpaisaRefCode(value);
                  }}
                  className="w-full px-3 py-2 border border-red-300 dark:border-red-700 rounded-lg bg-white dark:bg-gray-900 text-center font-mono text-lg tracking-widest"
                  data-testid="mpaisa-ref-code-input"
                />
                {mpaisaRefCode && mpaisaRefCode.length < 6 && (
                  <p className="text-xs text-red-500 dark:text-red-400 mt-1 text-center">
                    Code must be at least 6 digits
                  </p>
                )}
                {mpaisaRefCode && mpaisaRefCode.length >= 6 && (
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1 text-center flex items-center justify-center gap-1">
                    <Check className="w-3 h-3" /> Ready to verify
                  </p>
                )}
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                onClick={() => setShowPayment(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  // For cash payments, show the cash tender modal first
                  if (paymentMethod === 'cash') {
                    setShowCashTenderModal(true);
                  } else {
                    onProcessOrder(paymentMethod === 'mpaisa_qr' ? mpaisaRefCode : null, null, null, null, eftposCardType === 'overseas' ? overseasSurchargeAmount : 0);
                  }
                }}
                disabled={orderProcessing || (paymentMethod === 'mpaisa_qr' && (!mpaisaRefCode || mpaisaRefCode.length < 6))}
                className="bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="pos-confirm-order"
              >
                {orderProcessing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    {paymentMethod === 'cash' ? 'Enter Cash' : paymentMethod === 'mpaisa_qr' ? 'Verify & Confirm' : 'Confirm'}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
      
      {/* Cash Tender Modal */}
      <POSCashTenderModal
        isOpen={showCashTenderModal}
        onClose={() => setShowCashTenderModal(false)}
        orderTotal={totalWithVAT}
        orderProcessing={orderProcessing}
        onConfirm={(cashData) => {
          setLastCashTransaction(cashData);
          setShowCashTenderModal(false);
          // Show appropriate toast based on order total
          if (totalWithVAT === 0) {
            toast.success('Free order confirmed! ☕', {
              description: 'Bula Pass redemption processed',
              duration: 5000
            });
          } else {
            toast.success(`Change Due: $${cashData.changeDue.toFixed(2)}`, {
              description: `Received: $${cashData.amountTendered.toFixed(2)}`,
              duration: 5000
            });
          }
          // Process the order with cash data for receipt printing
          onProcessOrder(null, cashData, null, validatedPartner?.code || null, eftposCardType === 'overseas' ? overseasSurchargeAmount : 0);
        }}
      />
      
      {/* Manager Override Modal */}
      <ManagerOverrideModal
        isOpen={showOverrideModal}
        onClose={() => { setShowOverrideModal(false); setOverrideAction(null); }}
        onAuthorized={handleOverrideAuthorized}
        actionType={overrideAction?.type}
        cashierId={staff?.id || ''}
        cashierName={staff?.name || 'Cashier'}
        orderId={null}
        itemId={overrideAction?.item?.item_id}
        itemName={overrideAction?.item?.name}
        originalAmount={overrideAction?.item ? overrideAction.item.price * overrideAction.item.quantity : undefined}
        discountPercent={overrideAction?.discountPercent}
      />
      
      {/* Click outside to close item menu */}
      {activeItemMenu !== null && (
        <div 
          className="fixed inset-0 z-10" 
          onClick={() => setActiveItemMenu(null)}
        />
      )}
      
      {/* EFTPOS Card Type Modal - Local vs Overseas */}
      {showEftposCardTypeModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowEftposCardTypeModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="text-center mb-6">
              <CreditCard className="w-12 h-12 mx-auto mb-3 text-blue-600" />
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">EFTPOS Card Type</h3>
              <p className="text-gray-500 dark:text-gray-400 mt-1">Is this a local or overseas card?</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              {/* Local Card Button */}
              <button
                onClick={() => {
                  setEftposCardType('local');
                  setOverseasSurchargeAmount(0);
                  setShowEftposCardTypeModal(false);
                  setPaymentMethod('eftpos');
                }}
                className="flex flex-col items-center justify-center p-6 rounded-xl border-2 border-green-500 bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50 transition-all"
                data-testid="eftpos-local-card"
              >
                <span className="text-3xl mb-2">🇫🇯</span>
                <span className="font-bold text-green-700 dark:text-green-400 text-lg">Local Card</span>
                <span className="text-sm text-green-600 dark:text-green-500 mt-1">No surcharge</span>
              </button>
              
              {/* Overseas Card Button */}
              <button
                onClick={() => {
                  const surcharge = totalWithVAT * (OVERSEAS_SURCHARGE_PERCENT / 100);
                  setEftposCardType('overseas');
                  setOverseasSurchargeAmount(surcharge);
                  setShowEftposCardTypeModal(false);
                  setPaymentMethod('eftpos');
                }}
                className="flex flex-col items-center justify-center p-6 rounded-xl border-2 border-amber-500 bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-all"
                data-testid="eftpos-overseas-card"
              >
                <span className="text-3xl mb-2">🌏</span>
                <span className="font-bold text-amber-700 dark:text-amber-400 text-lg">Overseas Card</span>
                <span className="text-sm text-amber-600 dark:text-amber-500 mt-1">+4% surcharge</span>
                <span className="text-xs text-amber-600 dark:text-amber-500 font-medium mt-1">
                  +${(totalWithVAT * (OVERSEAS_SURCHARGE_PERCENT / 100)).toFixed(2)}
                </span>
              </button>
            </div>
            
            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Current Total: <span className="font-bold">${totalWithVAT.toFixed(2)}</span>
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Overseas cards incur a 4% bank surcharge
              </p>
            </div>
            
            <button
              onClick={() => setShowEftposCardTypeModal(false)}
              className="w-full mt-4 py-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
